import React, { FC, useMemo } from "react";
import {
  MDSActionMenuDropdown,
  MDSMenuDropdownSlotName,
  MDSButton,
  MDSIcon,
  MDSIconButton,
  MDSIconButtonSize,
  MDSIconKind,
  MDSConfigurationListRow,
  MDSConfigurationListRowSlotName,
  MDSLoadingSpinner,
  MDSSkeleton,
} from "@/mds";
import { CalendarConnectionConfigurationListItem } from "@/components/calendar/calendar-connection-configuration-list/calendar-connection-configuration-list-item";
import type { CalendarConnectionConfigurationListViewProps } from "./calendar-connection-configuration-list.types";
import {
  StyledAccountMenuWrapper,
  StyledSpinnerWrapper,
  StyledCalendarList,
  StyledCalendarSeparator,
  StyledDisconnectedText,
  StyledReconnectButtonWrapper,
  StyledRightAddonActionsWrapper,
} from "./calendar-connection-configuration-list.style";

export const CalendarConnectionConfigurationListView: FC<
  CalendarConnectionConfigurationListViewProps
> = ({
  email,
  provider,
  calendars,
  connectionStatus = "CONNECTED",
  onConnectionReconnect,
  accountMenu = [],
}) => {
  const title = email ?? <MDSSkeleton height={16} width={200} />;

  const getDescription = () => {
    switch (connectionStatus) {
      case "DISCONNECTING":
        return "Removing account...";
      case "LOADING":
        return "Getting calendars...";
      case "DISCONNECTED":
        return <StyledDisconnectedText>Disconnected</StyledDisconnectedText>;
      case "RECONNECTING":
        return <StyledDisconnectedText>Disconnected</StyledDisconnectedText>;
      case "CONNECTED":
      default:
        return "Connected";
    }
  };

  const isLoading = connectionStatus === "LOADING" || connectionStatus === "DISCONNECTING";
  const isDisconnected = connectionStatus === "DISCONNECTED" || connectionStatus === "RECONNECTING";
  const showReconnectButton = isDisconnected && !isLoading;
  const showAccountMenu = !isLoading && accountMenu && accountMenu.length > 0;

  // Sort calendars: primary/default calendars first, then alphabetically by label
  const sortedCalendars = useMemo(
    () =>
      [...calendars].sort((a, b) => {
        if (a.isPrimary !== b.isPrimary) {
          return a.isPrimary ? -1 : 1;
        }
        const nameA = (a.label ?? "").toLowerCase();
        const nameB = (b.label ?? "").toLowerCase();
        return nameA.localeCompare(nameB);
      }),
    [calendars]
  );

  return (
    <MDSConfigurationListRow title={title} description={getDescription()} isExpanded>
      <MDSIcon
        kind={provider === "google" ? MDSIconKind.GoogleCalendar : MDSIconKind.OutlookCalendar}
        slot={MDSConfigurationListRowSlotName.LeftAddon}
      />
      {isLoading && (
        <StyledSpinnerWrapper slot={MDSConfigurationListRowSlotName.RightAddon}>
          <MDSLoadingSpinner size={16} />
        </StyledSpinnerWrapper>
      )}
      {(showReconnectButton || showAccountMenu) && (
        <StyledRightAddonActionsWrapper slot={MDSConfigurationListRowSlotName.RightAddon}>
          {showReconnectButton && (
            <StyledReconnectButtonWrapper>
              <MDSButton
                onClick={onConnectionReconnect}
                isDisabled={connectionStatus === "RECONNECTING"}
              >
                Reconnect
              </MDSButton>
            </StyledReconnectButtonWrapper>
          )}
          {showAccountMenu && (
            <StyledAccountMenuWrapper>
              <MDSActionMenuDropdown contentList={accountMenu} placement="bottom right">
                <MDSIconButton
                  slot={MDSMenuDropdownSlotName.Trigger}
                  aria-label="Calendar account actions"
                  iconKind={MDSIconKind.More}
                  size={MDSIconButtonSize.Medium}
                />
              </MDSActionMenuDropdown>
            </StyledAccountMenuWrapper>
          )}
        </StyledRightAddonActionsWrapper>
      )}
      {!isLoading && (
        <StyledCalendarList
          slot={MDSConfigurationListRowSlotName.ExpandedContent}
          isDisabled={isDisconnected}
        >
          {sortedCalendars.map((calendar, index) => (
            <React.Fragment key={calendar.id}>
              <CalendarConnectionConfigurationListItem
                {...calendar}
                isDisabled={isDisconnected || calendar.isDisabled}
              />
              {index < sortedCalendars.length - 1 && (
                <StyledCalendarSeparator orientation="horizontal" />
              )}
            </React.Fragment>
          ))}
        </StyledCalendarList>
      )}
    </MDSConfigurationListRow>
  );
};
